// packages/report-components/src/textBlock.ts
//
// The text-block rule (plan 2026-09-09-the-editor-draws-with-the-reports-own-
// components, B1). It lived in `apps/frontend/src/components/reports/
// composition/` while the editor's canvas was the only surface that drew a
// text block from its stored content; fase 3 of ADR-0257 gives the reading
// room the same job, and fase 4 does it server-side in Node. The delivered report gets its text
// blocks from `text_block_jsx` / `text_to_jsx_children` in
// `apps/backend/services/reports/composition_adapter.py`: paragraphs split on
// a blank line, `**bold**` → <strong>, `*italic*` → <em>, `~~struck~~` → <s>,
// `[label](url)` → the label only (a user-controlled href in a delivered email
// is a phishing surface), everything else a literal. The link run carries its
// `href` as DATA (plan 2026-10-17-dashboard-text-style-complete, A3): a report
// and the email still draw only the label; a dashboard tile may draw an anchor
// for an http/https/mailto href, as its text blocks already do.
// This mirrors that rule so the canvas draws the same paragraphs — and both
// sides are pinned to ONE fixture, `packages/shared-types/src/fixtures/
// text-blocks.json`, by a Python test and by `textBlock.test.ts`.
//
// The output is DATA (runs), never markup: the canvas renders each run as a
// React node, so nothing here can become an element, an attribute or an
// event handler.

export type TextRun = { text: string; href?: string } | { strong: string } | { em: string } | { del: string };

// `_INLINE` in composition_adapter.py, transcribed. The capturing group is
// deliberate: `split` then returns the delimiters as their own parts, which
// is how the Python `re.split` behaves too.
const INLINE = /(\*\*(?!\s)[^*\n]+?(?<!\s)\*\*|\*(?!\s)[^*\n]+?(?<!\s)\*|~~(?!\s)[^~\n]+?(?<!\s)~~|\[[^\]\n]+?\]\([^)\n]*\))/;

/** An href a link may carry: http, https or mailto, nothing that could break
 *  out of an attribute. Anything else keeps the label only, so an unsafe URL
 *  never even reaches the runs (`_SAFE_HREF` in composition_adapter.py). */
const SAFE_HREF = /^(?:https?:\/\/|mailto:)[^\s<>"'`]+$/i;

/** Whether a link may keep its address (the rule above): the smart text
 *  editor writes a link back only when this holds. */
export const isSafeTextHref = (href: string): boolean => SAFE_HREF.test(href);

function runsOf(paragraph: string): TextRun[] {
  const out: TextRun[] = [];
  for (const part of paragraph.split(INLINE)) {
    if (!part) continue;
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      out.push({ strong: part.slice(2, -2) });
    } else if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      out.push({ em: part.slice(1, -1) });
    } else if (part.startsWith("~~") && part.endsWith("~~") && part.length > 4) {
      out.push({ del: part.slice(2, -2) });
    } else if (part.startsWith("[") && part.includes("](") && part.endsWith(")")) {
      const label = part.slice(1, part.indexOf("]("));
      const href = part.slice(part.indexOf("](") + 2, -1);
      out.push(SAFE_HREF.test(href) ? { text: label, href } : { text: label });
    } else {
      out.push({ text: part });
    }
  }
  return out;
}

/** The paragraphs of a text block, each a list of runs. Empty content is one
 *  empty paragraph — the adapter emits `<Text>{""}</Text>` for it, and a cell
 *  that draws nothing at all would look like a missing block. */
export function textBlockParagraphs(content: string | null | undefined): TextRun[][] {
  const paragraphs = (content ?? "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  if (paragraphs.length === 0) return [[]];
  return paragraphs.map(runsOf);
}

// ---------------------------------------------------------------------------
// Smart text (plan 2026-10-16-a-smart-text-block, S3; ADR-0426)
//
// The same paragraphs and runs, then each `{{name}}` inside a run replaced by
// the string the SERVER resolved for it (`services/composition/smart_text.py::
// resolve_smart_text`) as its own run, keeping the run's bold or italic. No
// formatting, arithmetic or period logic here: the client substitutes only.
// Values are substituted after the markdown parse, so a value can never
// become markup. The Python half is `smart_text.smart_text_paragraphs`; both
// are pinned by `packages/shared-types/src/fixtures/smart-text-blocks.json`.
// ---------------------------------------------------------------------------

export type SmartTextStatus =
  | "ok"
  | "no_value"
  | "no_comparison"
  | "partial_comparison"
  | "comparison_failed";

/** One variable as the server resolved it. */
export type ResolvedSmartTextVariable = { text: string; status: SmartTextStatus; fingerprint?: string };

/** A token's run. `pending`: no resolved value for this name yet (just
 *  added or redefined) — the tile draws a skeleton, a report writes "—". */
export type SmartTextVariableRun = {
  variable: string;
  text: string;
  status: SmartTextStatus | "pending";
  style?: "strong" | "em" | "del";
  /** Inside a `[label](url)`: the link's href, as data (see the top). */
  href?: string;
};

export type SmartTextRun = TextRun | SmartTextVariableRun;

// Only a valid name is a token; anything else was refused at the write and
// stays literal text if a renderer ever meets it.
const TOKEN = /\{\{([a-z][a-z0-9_]{0,31})\}\}/g;

function substitute(
  text: string,
  style: "strong" | "em" | "del" | undefined,
  resolved: Readonly<Record<string, ResolvedSmartTextVariable>>,
): SmartTextRun[] {
  const out: SmartTextRun[] = [];
  const literal = (s: string): TextRun =>
    style === "strong" ? { strong: s } : style === "em" ? { em: s } : style === "del" ? { del: s } : { text: s };
  let at = 0;
  for (const match of text.matchAll(TOKEN)) {
    const start = match.index ?? 0;
    if (start > at) out.push(literal(text.slice(at, start)));
    const name = match[1];
    const entry: unknown = Object.prototype.hasOwnProperty.call(resolved, name) ? resolved[name] : undefined;
    // Anything but {text: string, status: string} (an older cached shape) is
    // pending too; the Python half applies the same test.
    const usable =
      typeof entry === "object" &&
      entry !== null &&
      typeof (entry as { text?: unknown }).text === "string" &&
      typeof (entry as { status?: unknown }).status === "string";
    const run: SmartTextVariableRun = usable
      ? {
          variable: name,
          text: (entry as ResolvedSmartTextVariable).text,
          status: (entry as ResolvedSmartTextVariable).status,
        }
      : { variable: name, text: "—", status: "pending" };
    if (style) run.style = style;
    out.push(run);
    at = start + match[0].length;
  }
  if (at < text.length) out.push(literal(text.slice(at)));
  return out;
}

/** A smart text block's paragraphs: `textBlockParagraphs`, with every token
 *  replaced by its resolved run. */
export function smartTextParagraphs(
  content: string | null | undefined,
  resolved: Readonly<Record<string, ResolvedSmartTextVariable>>,
): SmartTextRun[][] {
  return textBlockParagraphs(content).map((runs) =>
    runs.flatMap((run): SmartTextRun[] =>
      "strong" in run
        ? substitute(run.strong, "strong", resolved)
        : "em" in run
          ? substitute(run.em, "em", resolved)
          : "del" in run
            ? substitute(run.del, "del", resolved)
            : run.href !== undefined
              ? // A link: its label substituted like any text, every run of it
                // carrying the href (only a dashboard tile draws it).
                substitute(run.text, undefined, resolved).map((r) => ({ ...r, href: run.href }) as SmartTextRun)
              : substitute(run.text, undefined, resolved),
    ),
  );
}
